import {FC, useEffect, useState, ReactNode} from 'react'
import type React from 'react'
import ReactApexChart from 'react-apexcharts'
import {useAuth} from '../../modules/auth'
import {api, AdminStats} from '../../services/api'
import {BRAND} from '../../brand'

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'https://api.qwiksurv.com/api').replace('/api', '')

interface DashUser {
  id: string
  firstName: string
  lastName: string
  email: string
  photo?: string
  role?: {name: string}
  createdAt: string
}

function resolvePhoto(photo?: string): string | null {
  if (!photo) return null
  return photo.startsWith('http') || photo.startsWith('data:') ? photo : `${API_ORIGIN}${photo}`
}

function userInitials(u: DashUser) {
  return ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() || '?'
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)   return 'just now'
  if (mins < 60)  return `${mins} min${mins > 1 ? 's' : ''} ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs} hour${hrs > 1 ? 's' : ''} ago`
  const days = Math.floor(hrs / 24)
  return `${days} day${days > 1 ? 's' : ''} ago`
}

const fmtNum = (n: number) => (n || 0).toLocaleString()

const AVATAR_COLORS = ['#f46a47', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e']
function avatarBg(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

const roleLabel = (r?: string) => {
  if (r === 'driver') return {text: 'Driver', bg: '#eff6ff', color: '#2563eb'}
  if (r === 'business') return {text: 'Business', bg: BRAND.primaryLight, color: BRAND.primaryActive}
  return {text: 'Customer', bg: '#f0fdf4', color: '#16a34a'}
}

const UserAvatar: FC<{user: DashUser; size?: number}> = ({user, size = 44}) => {
  const [failed, setFailed] = useState(false)
  const url = !failed ? resolvePhoto(user.photo) : null
  if (url) {
    return (
      <img src={url} alt={user.firstName} onError={() => setFailed(true)}
        style={{width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}} />
    )
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: avatarBg(user.firstName + user.lastName),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.3,
    }}>
      {userInitials(user)}
    </div>
  )
}

// ── Icons ────────────────────────────────────────────────────────────────────
const usersIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/>
    <path d='M23 21v-2a4 4 0 0 0-3-3.87'/><path d='M16 3.13a4 4 0 0 1 0 7.75'/>
  </svg>
)
const driverIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M5 17H3v-4l2-5h11l4 5v4h-2'/><circle cx='7.5' cy='17' r='2'/><circle cx='16.5' cy='17' r='2'/>
  </svg>
)
const businessIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M3 9l1-5h16l1 5'/><path d='M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9'/>
    <path d='M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0'/>
  </svg>
)
const bookingIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M9 11l3 3L22 4'/><path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'/>
  </svg>
)

interface StatCard {
  label: string
  value: string
  sub?: string
  subColor?: string
  bg: string
  iconColor: string
  icon: ReactNode
}

function buildStats(s: AdminStats | null): StatCard[] {
  return [
    {label: 'Total Users', value: s ? fmtNum(s.users) : '—', sub: 'Customer app', subColor: '#94a3b8',
     bg: '#f0fdf4', iconColor: '#16a34a', icon: usersIcon},
    {label: 'Drivers', value: s ? fmtNum(s.drivers) : '—',
     sub: s ? `${fmtNum(s.driversOnline)} online now` : undefined, subColor: '#16a34a',
     bg: '#eff6ff', iconColor: '#2563eb', icon: driverIcon},
    {label: 'Businesses', value: s ? fmtNum(s.businesses) : '—', sub: 'Service providers', subColor: '#94a3b8',
     bg: BRAND.primaryLight, iconColor: BRAND.primary, icon: businessIcon},
    {label: 'Total Bookings', value: s ? fmtNum(s.bookings) : '—', sub: 'All-time', subColor: '#94a3b8',
     bg: '#faf5ff', iconColor: '#a855f7', icon: bookingIcon},
  ]
}

// Booking status → label + colour. Matches the backend BookingStatus enum.
const STATUS_META: Record<string, {label: string; color: string; bg: string}> = {
  pending:     {label: 'Pending',     color: '#a16207', bg: '#fef9c3'},
  accepted:    {label: 'Accepted',    color: '#2563eb', bg: '#eff6ff'},
  in_progress: {label: 'In Progress', color: '#7c3aed', bg: '#f3e8ff'},
  completed:   {label: 'Completed',   color: '#16a34a', bg: '#dcfce7'},
  cancelled:   {label: 'Cancelled',   color: '#dc2626', bg: '#fee2e2'},
}
const STATUS_ORDER = ['pending', 'accepted', 'in_progress', 'completed', 'cancelled']

const CARD: React.CSSProperties = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

// Shared donut config. `centerLabel` shows the grand total in the hole.
const donutOptions = (labels: string[], colors: string[], centerLabel: string): ApexCharts.ApexOptions => ({
  chart: {type: 'donut', fontFamily: 'inherit'},
  labels,
  colors,
  legend: {position: 'bottom', fontSize: '13px', markers: {width: 10, height: 10, radius: 6} as any, itemMargin: {horizontal: 8, vertical: 4}},
  dataLabels: {enabled: false},
  stroke: {width: 2, colors: ['#fff']},
  plotOptions: {
    pie: {donut: {size: '68%', labels: {
      show: true,
      value: {fontSize: '24px', fontWeight: 700, color: '#0f172a', offsetY: 4},
      total: {show: true, label: centerLabel, fontSize: '12px', color: '#94a3b8',
        formatter: (w: any) => `${w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)}`},
    }}},
  },
  tooltip: {y: {formatter: (v: number) => `${v}`}},
  noData: {text: 'No data yet', style: {color: '#94a3b8', fontSize: '13px'}},
})

// A bar chart of new signups over the last 7 days, split by role. Derived from
// the recent-signups list we already load, so no extra endpoint is needed.
const signupsBarOptions = (cats: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'bar', stacked: true, toolbar: {show: false}, fontFamily: 'inherit'},
  plotOptions: {bar: {borderRadius: 4, columnWidth: '45%'}},
  colors: ['#16a34a', '#2563eb', BRAND.primary],
  dataLabels: {enabled: false},
  xaxis: {categories: cats, axisBorder: {show: false}, axisTicks: {show: false}, labels: {style: {fontSize: '11px'}}},
  yaxis: {labels: {formatter: (v) => `${Math.round(v)}`}},
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  legend: {position: 'top', fontSize: '13px', markers: {width: 10, height: 10, radius: 6} as any},
  tooltip: {y: {formatter: (v) => `${v}`}},
})

const DAY_MS = 86400000
const roleKey = (r?: string) => (r === 'driver' ? 'driver' : r === 'business' ? 'business' : 'user')

const AdminDashboard: FC = () => {
  const {currentUser} = useAuth()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [signupUsers, setSignupUsers] = useState<DashUser[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    Promise.all([
      api.getAdminStats(),
      // Pull a wider page so we can both list the latest 6 and bucket the last
      // 7 days for the signups chart from the same request.
      api.getUsersPaged(1, 100, ''),
    ])
      .then(([s, users]) => {
        if (!alive) return
        setStats(s)
        setSignupUsers(users.items as unknown as DashUser[])
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [])

  const cards = buildStats(stats)
  const providers = stats ? stats.drivers + stats.businesses : 0
  const bookingsByStatus = stats?.bookingsByStatus ?? {}
  const recentUsers = signupUsers.slice(0, 6)

  // Donut series
  const accountSeries = stats ? [stats.users, stats.drivers, stats.businesses] : [0, 0, 0]
  const bookingSeries = STATUS_ORDER.map((k) => bookingsByStatus[k] ?? 0)

  // Signups over the last 7 days, split by role, from the loaded list.
  const now = Date.now()
  const dayCats: string[] = []
  const dayBuckets: Record<string, number[]> = {user: [], driver: [], business: []}
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now - i * DAY_MS)
    dayCats.push(d.toLocaleDateString(undefined, {weekday: 'short'}))
    dayBuckets.user.push(0); dayBuckets.driver.push(0); dayBuckets.business.push(0)
  }
  for (const u of signupUsers) {
    const daysAgo = Math.floor((now - new Date(u.createdAt).getTime()) / DAY_MS)
    if (daysAgo >= 0 && daysAgo <= 6) {
      dayBuckets[roleKey(u.role?.name)][6 - daysAgo] += 1
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 28}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Dashboard</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Welcome back, {currentUser?.first_name}! Here's how Qwiksurv is doing.
        </p>
      </div>

      {/* Stat cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24}}>
        {cards.map((s) => (
          <div key={s.label} style={{...CARD, padding: '20px 20px'}}>
            <div style={{width: 44, height: 44, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.iconColor, marginBottom: 16}}>
              {s.icon}
            </div>
            <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>{s.value}</div>
            <div style={{fontSize: 13, color: '#94a3b8'}}>{s.label}</div>
            {s.sub ? <div style={{fontSize: 12, color: s.subColor ?? '#94a3b8', fontWeight: 600, marginTop: 6}}>{s.sub}</div> : null}
          </div>
        ))}
      </div>

      {/* Charts: account distribution + bookings by status (donuts) */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24}}>
        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 8px'}}>
            Account Distribution <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· by role</span>
          </h3>
          <ReactApexChart
            type='donut'
            series={accountSeries}
            options={donutOptions(['Users', 'Drivers', 'Businesses'], ['#16a34a', '#2563eb', BRAND.primary], 'Accounts')}
            height={300}
          />
        </div>

        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 8px'}}>
            Bookings by Status <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· {stats ? fmtNum(stats.bookings) : '—'} total</span>
          </h3>
          <ReactApexChart
            type='donut'
            series={bookingSeries}
            options={donutOptions(
              STATUS_ORDER.map((k) => STATUS_META[k].label),
              STATUS_ORDER.map((k) => STATUS_META[k].color),
              'Bookings',
            )}
            height={300}
          />
        </div>
      </div>

      {/* Signups over the last 7 days (stacked by role) + marketplace glance */}
      <div style={{display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20, marginBottom: 24}}>
        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 8px'}}>
            New Signups <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· last 7 days</span>
          </h3>
          <ReactApexChart
            type='bar'
            series={[
              {name: 'Users', data: dayBuckets.user},
              {name: 'Drivers', data: dayBuckets.driver},
              {name: 'Businesses', data: dayBuckets.business},
            ]}
            options={signupsBarOptions(dayCats)}
            height={280}
          />
        </div>

        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 16px'}}>
            Marketplace at a glance
          </h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{border: '1px solid #e2e8f0', borderRadius: 10, padding: 16}}>
              <div style={{fontSize: 12, color: '#94a3b8', marginBottom: 4}}>Customer App</div>
              <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a'}}>{stats ? fmtNum(stats.users) : '—'}</div>
              <div style={{fontSize: 12, color: '#94a3b8', marginTop: 4}}>users requesting services</div>
            </div>
            <div style={{border: '1px solid #e2e8f0', borderRadius: 10, padding: 16}}>
              <div style={{fontSize: 12, color: '#94a3b8', marginBottom: 4}}>Provider App</div>
              <div style={{fontSize: 26, fontWeight: 700, color: BRAND.primary}}>{stats ? fmtNum(providers) : '—'}</div>
              <div style={{fontSize: 12, color: '#94a3b8', marginTop: 4}}>
                {stats ? `${fmtNum(stats.drivers)} drivers · ${fmtNum(stats.businesses)} businesses` : 'drivers + businesses'}
              </div>
            </div>
            <div style={{padding: '12px 14px', background: '#f8fafc', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10}}>
              <span style={{width: 8, height: 8, borderRadius: '50%', background: '#16a34a', flexShrink: 0}} />
              <span style={{fontSize: 13, color: '#475569'}}>
                <b>{stats ? fmtNum(stats.driversOnline) : '—'}</b> driver{stats?.driversOnline === 1 ? '' : 's'} online now
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Signups (across all roles) */}
      <div style={{...CARD, overflow: 'hidden'}}>
        <div style={{padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Recent Signups</h3>
        </div>
        {loading && (
          <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>Loading…</div>
        )}
        {!loading && recentUsers.length === 0 && (
          <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>No signups yet</div>
        )}
        {recentUsers.map((u, i) => {
          const rl = roleLabel(u.role?.name)
          return (
            <div key={u.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: i < recentUsers.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <UserAvatar user={u} size={44} />
                <div>
                  <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a'}}>{u.firstName} {u.lastName}</div>
                  <div style={{fontSize: 12, color: '#94a3b8'}}>{u.email}</div>
                </div>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <span style={{fontSize: 11, fontWeight: 600, color: rl.color, background: rl.bg, padding: '3px 10px', borderRadius: 20}}>{rl.text}</span>
                <div style={{fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap'}}>{timeAgo(u.createdAt)}</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const AdminDashboardWrapper: FC = () => <AdminDashboard />

export {AdminDashboardWrapper}
